/**
 * NtsbRecommendationsTab Component
 *
 * The workbook's NTSB Recommendations register: every NTSB safety recommendation tied to the
 * workbook's reports, either because a report's investigation issued it or because a later
 * investigation reiterated it. Read from the FAA's side (its own status, how long it has been
 * open, the NTSB's assessment of its response), filterable, with a chat about whatever the
 * table currently shows.
 *
 * Recommendations are tied to reports, not to fault tree nodes, so nothing here claims a
 * recommendation is about a particular node (see GitLab #1562 for node targeting).
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Fab,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import {
    RegisterRecommendationDTO,
    SafetyRecommendationLetterDTO,
    SafetyRecommendationStatusGroup,
} from '../../../../../../../@types/safety-recommendation';
import {
    DEFAULT_NTSB_REGISTER_FILTERS,
    NtsbRegisterFilters,
    PersistedChatMessage,
    PrimaryFilter,
    useIRPWorkbookStore,
    useIRPWorkbookStoreApi,
    useNtsbRecommendationsCache,
    useNtsbRegisterFilters,
    usePrimaryFilter,
} from '../../../../../../../stores/irp-workbook.store';
import {useIRPContext} from '../../IRPWorkbookProvider';
import LLMChatDialog, {ChatMessage} from '../../../../../../common/LLMChatDialog';
import RecommendationOverview, {RecommendationStatusChip, STATUS_STYLE} from '../../../../../../safety-recommendations/RecommendationOverview';
import CorrespondenceDialog from '../../../../../../safety-recommendations/CorrespondenceDialog';
import RecommendationSummary from '../../../../../../safety-recommendations/RecommendationSummary';
import safetyRecommendationService from '../../../../../../../services/safety-recommendation.service';
import {
    ageYears,
    ANY_STATUS,
    countInvestigations,
    displayStatus,
    focusStatus,
    groupsForSelection,
    isAddressedToFaa,
    isOpen,
    IssuedByYear,
    MIXED_STATUS,
    STATUS_GROUP_LABELS,
    STATUS_GROUP_ORDER,
    STATUS_PRESETS,
    statusSelection,
} from '../../../../../../safety-recommendations/recommendations';
import {
    applyNonStatusFilters,
    applyRegisterFilters,
    countByGroup,
    describeFilters,
    formatRegisterForPrompt,
    issuedByYear,
    sortRegister,
} from '../../utils/ntsbRecommendations';

/** The register table never gets shorter than this; a shorter window scrolls the whole tab. */
const MIN_TABLE_HEIGHT = 420;

const SUGGESTED_PROMPTS = [
    'Which of these has the FAA left open longest, and what did the NTSB last say about each?',
    'Where did the NTSB judge the FAA\'s action unacceptable, and why?',
    'Which recommendations were reiterated after later accidents in this workbook?',
];

/** One NTSB letter in the expanded row. */
const Letter: React.FC<{ title: string; letter?: SafetyRecommendationLetterDTO; empty: string }> = ({title, letter, empty}) => (
    <Box sx={{flex: 1, minWidth: 260}}>
        <Typography variant="subtitle2">{title}{letter?.date ? ` (${letter.date})` : ''}</Typography>
        {letter?.contents ? (
            <Typography variant="body2" sx={{whiteSpace: 'pre-line', maxHeight: 220, overflowY: 'auto', mt: 0.5}}>
                {letter.contents}
            </Typography>
        ) : (
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{empty}</Typography>
        )}
    </Box>
);

const NtsbRecommendationsTab: React.FC = () => {
    const {dataSource, componentMapping} = useIRPContext();
    const storeApi = useIRPWorkbookStoreApi();
    const primaryFilter = usePrimaryFilter();
    const recommendations = useNtsbRecommendationsCache();
    const setCache = useIRPWorkbookStore((state) => state.setNtsbRecommendationsCache);
    const filters = useNtsbRegisterFilters();
    const setFilters = useIRPWorkbookStore((state) => state.setNtsbRegisterFilters);
    const chatHistory = useIRPWorkbookStore((state) => state.ntsbChatHistory);
    const setChatHistory = useIRPWorkbookStore((state) => state.setNtsbChatHistory);
    const chatContextSent = useIRPWorkbookStore((state) => state.ntsbChatContextSent);
    const setChatContextSent = useIRPWorkbookStore((state) => state.setNtsbChatContextSent);
    const navigateToReportAnalysis = useIRPWorkbookStore((state) => state.navigateToReportAnalysis);

    const [loadError, setLoadError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [chatOpen, setChatOpen] = useState(false);

    // The correspondence dialog: its recommendation, and its letters once loaded
    const [correspondenceFor, setCorrespondenceFor] = useState<RegisterRecommendationDTO | null>(null);
    const [letters, setLetters] = useState<SafetyRecommendationLetterDTO[] | null>(null);
    const [lettersError, setLettersError] = useState<string | null>(null);
    const openingFor = useRef<number | null>(null);
    const openCorrespondence = (rec: RegisterRecommendationDTO): void => {
        openingFor.current = rec.id;
        setCorrespondenceFor(rec);
        setLetters(null);
        setLettersError(null);
        safetyRecommendationService.getCorrespondence(rec)
            .then((loaded): void => { if (openingFor.current === rec.id) setLetters(loaded); })
            .catch((error: Error): void => { if (openingFor.current === rec.id) setLettersError(error.message); });
    };
    const closeCorrespondence = (): void => {
        openingFor.current = null;
        setCorrespondenceFor(null);
    };
    const today = useMemo((): Date => new Date(), []);

    // Fetch on first view and whenever a primary filter change clears the cache. A failed fetch
    // is not retried on its own until the primary filter changes, so the error stays on screen;
    // Retry (a new attempt number) fetches again.
    const failedForFilter = useRef<PrimaryFilter | null>(null);
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!dataSource || recommendations !== null || failedForFilter.current === primaryFilter) return;
        const {dateFrom, dateTo, selectedDescriptors, selectedSources} = primaryFilter;
        const fail = (message: string): void => {
            failedForFilter.current = primaryFilter;
            setLoadError(message);
        };
        setLoadError(null);
        dataSource.getWorkbookNtsbRecommendations(
            dateFrom,
            dateTo,
            selectedDescriptors.size > 0 ? Array.from(selectedDescriptors) : null,
            selectedSources.size > 0 ? Array.from(selectedSources) : null,
        )
            .then((data: unknown): void => {
                // A backend without this endpoint forwards the unknown path to the web app's index
                // page, which arrives as a successful HTML response rather than an error.
                if (Array.isArray(data)) {
                    setCache(data as RegisterRecommendationDTO[]);
                } else {
                    fail('The server did not return NTSB recommendations. It may need restarting to pick up the NTSB register.');
                }
            })
            .catch((error: Error): void => fail(error.message || 'Failed to load NTSB recommendations'));
    }, [dataSource, recommendations, primaryFilter, setCache, attempt]);

    const retry = (): void => {
        failedForFilter.current = null;
        setAttempt((n) => n + 1);
    };

    const all = useMemo((): RegisterRecommendationDTO[] => recommendations ?? [], [recommendations]);
    const statusCounts = useMemo(
        (): Record<SafetyRecommendationStatusGroup, number> => countByGroup(applyNonStatusFilters(all, filters)),
        [all, filters]
    );
    const shown = useMemo(
        (): RegisterRecommendationDTO[] => sortRegister(applyRegisterFilters(all, filters), filters, today),
        [all, filters, today]
    );
    // The years of what the table is showing, not of everything but the year filter: a range is
    // set in the year inputs, so there is nothing to click your way back to.
    const byYear = useMemo((): IssuedByYear => issuedByYear(shown), [shown]);

    const contextPrepend = useMemo((): string => {
        if (shown.length === 0) return '';
        return `Context: NTSB Safety Recommendations Register for this workbook

These are the NTSB safety recommendations tied to this workbook's reports that the register table currently shows (${shown.length} of ${all.length}). A report is tied to a recommendation when its NTSB investigation issued it, or when its investigation reiterated it: the NTSB found the recommendation still applied after that later accident. Status is the FAA's own status where the FAA is an addressee. The table filters may have changed since earlier answers, so answer from this set; when it does not cover something, say so.
Active filters: ${describeFilters(filters)}

${formatRegisterForPrompt(shown, today)}

---

User Question: `;
    }, [shown, all.length, filters, today]);

    // Tell the user when the chat's set changes under an ongoing conversation. The history is
    // read from the store when the set changes, so appending the note does not re-trigger this.
    // Only a loaded register counts: arriving data is not the set changing.
    const shownSignature = shown.map((rec) => rec.id).join(',');
    const shownCount = shown.length;
    const loaded = recommendations !== null;
    const previousSignature = useRef<string | null>(null);
    useEffect(() => {
        if (!loaded) return;
        const previous = previousSignature.current;
        previousSignature.current = shownSignature;
        if (previous === null || previous === shownSignature) return;
        const {ntsbChatHistory, setNtsbChatHistory} = storeApi.getState();
        if (!ntsbChatHistory.some((m) => m.role === 'user')) return;
        setNtsbChatHistory([...ntsbChatHistory, {
            role: 'system',
            content: `Filters changed: the chat now sees the ${shownCount} recommendation${shownCount === 1 ? '' : 's'} shown.`,
        }]);
    }, [shownSignature, shownCount, loaded, storeApi]);

    const updateFilters = (changes: Partial<NtsbRegisterFilters>): void => setFilters({...filters, ...changes});

    const toggleStatus = (group: SafetyRecommendationStatusGroup): void => {
        const groups = filters.statusGroups.includes(group)
            ? filters.statusGroups.filter((g) => g !== group)
            : [...filters.statusGroups, group];
        updateFilters({statusGroups: groups});
    };

    // A single issue year is "selected" when the year range is exactly that year.
    const selectedYear = filters.issuedFromYear !== null && filters.issuedFromYear === filters.issuedToYear
        ? filters.issuedFromYear : null;
    const toggleYear = (year: number): void => {
        const clear = selectedYear === year;
        updateFilters({issuedFromYear: clear ? null : year, issuedToYear: clear ? null : year});
    };

    const toggleSort = (sortBy: NtsbRegisterFilters['sortBy']): void => {
        const sortDirection = filters.sortBy === sortBy && filters.sortDirection === 'desc' ? 'asc' : 'desc';
        updateFilters({sortBy, sortDirection});
    };

    const toggleExpanded = (id: number): void => {
        const next = new Set(expanded);
        if (next.has(id)) next.delete(id); else next.add(id);
        setExpanded(next);
    };

    const yearInput = (value: number | null, onChange: (year: number | null) => void, label: string): React.ReactElement => (
        <TextField
            size="small"
            label={label}
            value={value ?? ''}
            inputProps={{inputMode: 'numeric', maxLength: 4}}
            onChange={(e): void => {
                const digits = e.target.value.replace(/\D/g, '');
                onChange(digits ? Number(digits) : null);
            }}
            sx={{width: 104}}
        />
    );

    if (loadError) {
        return (
            <Box sx={{p: 3}}>
                <Typography color="error">{loadError}</Typography>
                <Button size="small" variant="outlined" sx={{mt: 1.5}} onClick={retry}>Retry</Button>
            </Box>
        );
    }
    if (recommendations === null) {
        return <Box sx={{p: 3, display: 'flex', justifyContent: 'center'}}><CircularProgress size={28} /></Box>;
    }
    if (all.length === 0) {
        return (
            <Box sx={{p: 3}}>
                <Typography variant="body2" color="text.secondary">
                    {componentMapping ? (
                        // A component is mapped against one named source, and only NTSB investigation
                        // reports can carry a recommendation, so for any other source this is expected.
                        `None of the ${componentMapping.sourceName} reports this version is mapped against come from an NTSB investigation that issued or reiterated a safety recommendation. Only NTSB investigation reports can, and most investigations issue none. If ${componentMapping.sourceName} is an NTSB source and you expected some, the local copy of NTSB recommendations may not have been synced yet.`
                    ) : (
                        'None of this workbook\'s reports come from an NTSB investigation that issued or reiterated a safety recommendation. Most NTSB investigations issue none. If you expected some, the local copy of NTSB recommendations may not have been synced yet.'
                    )}
                </Typography>
            </Box>
        );
    }

    return (
        // The outer box anchors the floating chat and its button; the inner one scrolls the page when the window is short
        <Box sx={{position: 'relative', height: '100%', minHeight: 0}}>
            <Box
                data-testid="ntsb-register-scroll"
                sx={{
                    height: '100%', overflowY: 'auto', boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.5,
                }}
            >
                {/* The register at a glance; both charts double as filters */}
                <RecommendationOverview
                    shownCount={shown.length}
                    totalCount={all.length}
                    investigationCount={countInvestigations(shown)}
                    statusCounts={statusCounts}
                    byYear={byYear}
                    selectedStatuses={filters.statusGroups}
                    issuedFromYear={filters.issuedFromYear}
                    issuedToYear={filters.issuedToYear}
                    onToggleStatus={toggleStatus}
                    onToggleYear={toggleYear}
                />

                {/* Filters */}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <TextField
                        size="small"
                        label="Search"
                        placeholder="Number, text, addressee, report"
                        value={filters.search}
                        onChange={(e): void => updateFilters({search: e.target.value})}
                        sx={{flex: '1 1 200px', maxWidth: 320}}
                    />
                    <FormControl size="small" sx={{width: 230}}>
                        <InputLabel id="ntsb-status">FAA status</InputLabel>
                        <Select
                            labelId="ntsb-status"
                            label="FAA status"
                            value={statusSelection(filters.statusGroups)}
                            onChange={(e): void => {
                                if (e.target.value !== MIXED_STATUS) updateFilters({statusGroups: groupsForSelection(e.target.value)});
                            }}
                            renderValue={(value: string): string => {
                                if (value === MIXED_STATUS) return filters.statusGroups.map((g) => STATUS_GROUP_LABELS[g]).join(', ');
                                if (value === ANY_STATUS) return 'Any status';
                                return STATUS_PRESETS.find((p) => p.key === value)?.label
                                ?? STATUS_GROUP_LABELS[value as SafetyRecommendationStatusGroup];
                            }}
                        >
                            <MenuItem value={ANY_STATUS}>Any status</MenuItem>
                            {STATUS_PRESETS.map((preset) => (
                                <MenuItem key={preset.key} value={preset.key}>{preset.label}</MenuItem>
                            ))}
                            <Divider />
                            {STATUS_GROUP_ORDER.map((group) => {
                                const {color, Icon} = STATUS_STYLE[group];
                                return (
                                    <MenuItem key={group} value={group}>
                                        <Icon sx={{fontSize: 18, color, mr: 1}} />
                                        {STATUS_GROUP_LABELS[group]}
                                    </MenuItem>
                                );
                            })}
                            {/* The value shown while the chart holds a combination with no name; not pickable */}
                            <MenuItem value={MIXED_STATUS} sx={{display: 'none'}}>Combination</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{width: 140}}>
                        <InputLabel id="ntsb-addressed-to">Addressed to</InputLabel>
                        <Select
                            labelId="ntsb-addressed-to"
                            label="Addressed to"
                            value={filters.addressedTo}
                            onChange={(e): void => updateFilters({addressedTo: e.target.value as NtsbRegisterFilters['addressedTo']})}
                        >
                            <MenuItem value="all">Anyone</MenuItem>
                            <MenuItem value="faa">The FAA</MenuItem>
                            <MenuItem value="notFaa">Not the FAA</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{width: 200}}>
                        <InputLabel id="ntsb-relation">Tied to the reports by</InputLabel>
                        <Select
                            labelId="ntsb-relation"
                            label="Tied to the reports by"
                            value={filters.relation}
                            onChange={(e): void => updateFilters({relation: e.target.value as NtsbRegisterFilters['relation']})}
                        >
                            <MenuItem value="all">Issued or reiterated</MenuItem>
                            <MenuItem value="ISSUED">Issued by their investigation</MenuItem>
                            <MenuItem value="REITERATED">Reiterated after their accident</MenuItem>
                        </Select>
                    </FormControl>
                    {yearInput(filters.issuedFromYear, (year): void => updateFilters({issuedFromYear: year}), 'Issued from')}
                    {yearInput(filters.issuedToYear, (year): void => updateFilters({issuedToYear: year}), 'Issued to')}
                    <FormControlLabel
                        sx={{mr: 0}}
                        control={
                            <Switch
                                size="small"
                                checked={filters.showSuperseded}
                                onChange={(e): void => updateFilters({showSuperseded: e.target.checked})}
                            />
                        }
                        label={<Typography variant="body2">Show superseded</Typography>}
                    />
                    <Button size="small" onClick={(): void => setFilters(DEFAULT_NTSB_REGISTER_FILTERS)}>Reset</Button>
                </Stack>

                {/* Register: fixed columns, the recommendation takes the remaining width. It fills the space left
                but never drops below a readable height: past that, the whole tab scrolls instead. */}
                <TableContainer
                    data-testid="ntsb-register-table"
                    sx={{flex: `1 0 ${MIN_TABLE_HEIGHT}px`, minHeight: MIN_TABLE_HEIGHT, border: 1, borderColor: 'divider', borderRadius: 1}}
                >
                    <Table size="small" stickyHeader aria-label="NTSB recommendations" sx={{tableLayout: 'fixed', minWidth: 900}}>
                        <colgroup>
                            <col style={{width: 40}} />
                            <col />
                            <col style={{width: 96}} />
                            <col style={{width: 168}} />
                            <col style={{width: 88}} />
                            <col style={{width: 150}} />
                            <col style={{width: 150}} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>
                                    <TableSortLabel active={filters.sortBy === 'number'} direction={filters.sortDirection}
                                        onClick={(): void => toggleSort('number')}>
                                    Recommendation
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel active={filters.sortBy === 'issued'} direction={filters.sortDirection}
                                        onClick={(): void => toggleSort('issued')}>
                                    Issued
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>FAA status</TableCell>
                                <TableCell>
                                    <TableSortLabel active={filters.sortBy === 'yearsOpen'} direction={filters.sortDirection}
                                        onClick={(): void => toggleSort('yearsOpen')}>
                                    Time
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Other addressees</TableCell>
                                <TableCell>Workbook reports</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                        No recommendations match these filters.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {shown.map((rec) => {
                                const open = expanded.has(rec.id);
                                const group = rec.focusStatusGroup;
                                const years = ageYears(rec, today);
                                return (
                                    <React.Fragment key={rec.id}>
                                        <TableRow hover sx={{'& > td': {borderBottom: open ? 'unset' : undefined}, verticalAlign: 'top'}}>
                                            <TableCell padding="checkbox">
                                                <IconButton size="small" aria-label={`Details for ${rec.number}`}
                                                    onClick={(): void => toggleExpanded(rec.id)}>
                                                    {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <RecommendationSummary rec={rec} onOpenLetters={(): void => openCorrespondence(rec)} />
                                            </TableCell>
                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{rec.issuedDate}</TableCell>
                                            <TableCell>
                                                <RecommendationStatusChip
                                                    group={group}
                                                    label={displayStatus(focusStatus(rec)) || 'Status unknown'}
                                                    onClick={(): void => openCorrespondence(rec)}
                                                    ariaLabel={`${displayStatus(focusStatus(rec)) || 'Status unknown'}: read the ${rec.number} correspondence`}
                                                />
                                                {!isAddressedToFaa(rec) && (
                                                    <Typography variant="caption" color="text.secondary" component="div">
                                                    Not addressed to the FAA (overall status)
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {years !== null && (
                                                    <>
                                                        <Typography variant="body2">{years.toFixed(1)} yrs</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isOpen(rec) ? 'open so far' : 'to close'}
                                                        </Typography>
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {rec.otherAddressees.map((a) => a.name).join('; ')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                                    {rec.linkedReports.map((report) => (
                                                        <Tooltip key={`${report.reportId}-${report.relation}`}
                                                            title={report.relation === 'REITERATED'
                                                                ? 'This later accident\'s investigation reiterated the recommendation'
                                                                : 'This report\'s investigation issued the recommendation'}>
                                                            <Chip
                                                                size="small"
                                                                clickable
                                                                variant={report.relation === 'REITERATED' ? 'outlined' : 'filled'}
                                                                color={report.relation === 'REITERATED' ? 'secondary' : 'default'}
                                                                label={report.relation === 'REITERATED' ? `${report.uniqueId} (reiterated)` : report.uniqueId}
                                                                onClick={(): void => navigateToReportAnalysis(report.reportId)}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{py: 0}}>
                                                <Collapse in={open} timeout="auto" unmountOnExit>
                                                    <Stack spacing={1.5} sx={{py: 1.5, pl: 6}}>
                                                        <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
                                                            <Letter title="NTSB assessment" letter={rec.assessment}
                                                                empty="No assessment letter from the NTSB yet." />
                                                            <Letter title={`Latest response${rec.latestResponse?.addressee ? ` from ${rec.latestResponse.addressee}` : ''}`}
                                                                letter={rec.latestResponse} empty="No response on record." />
                                                        </Stack>
                                                        {rec.otherAddressees.length > 0 && (
                                                            <Box>
                                                                <Typography variant="subtitle2">Other addressees</Typography>
                                                                {rec.otherAddressees.map((a) => (
                                                                    <Typography key={a.name} variant="body2">
                                                                        {a.name}: {displayStatus(a.status) || 'status unknown'}{a.closedDate ? ` (closed ${a.closedDate})` : ''}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}
                                                        {(rec.caseSummary || rec.caseProbableCause) && (
                                                            <Box>
                                                                <Typography variant="subtitle2">Investigation {rec.issuingCaseNumber}</Typography>
                                                                {rec.caseSummary && <Typography variant="body2">{rec.caseSummary}</Typography>}
                                                                {rec.caseProbableCause && (
                                                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                                                        <strong>Probable cause:</strong> {rec.caseProbableCause}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                                                            {rec.keywords && <Typography variant="caption">Keywords: {rec.keywords}</Typography>}
                                                            {rec.caseLink && (
                                                                <Link variant="caption" href={rec.caseLink} target="_blank" rel="noopener noreferrer">
                                                                Investigation page{rec.caseReportNumber ? ` (${rec.caseReportNumber})` : ''}
                                                                </Link>
                                                            )}
                                                            {rec.letterLink && (
                                                                <Link variant="caption" href={rec.letterLink} target="_blank" rel="noopener noreferrer">
                                                                Recommendation letter (PDF)
                                                                </Link>
                                                            )}
                                                            <Link component="button" variant="caption" onClick={(): void => openCorrespondence(rec)}>
                                                            Read all correspondence
                                                            </Link>
                                                        </Stack>
                                                    </Stack>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <CorrespondenceDialog
                recommendation={correspondenceFor}
                letters={letters}
                error={lettersError}
                onClose={closeCorrespondence}
            />

            {!chatOpen && (
                <Fab
                    variant="extended"
                    color="primary"
                    size="medium"
                    onClick={(): void => setChatOpen(true)}
                    sx={{position: 'absolute', right: 24, bottom: 24, zIndex: 4}}
                >
                    <AutoAwesomeIcon sx={{mr: 1}} />
                    Ask about these
                </Fab>
            )}

            {/* Conversation, floating over the table. Kept mounted while hidden so an answer in progress still lands. */}
            <Paper
                elevation={8}
                role="dialog"
                aria-label="Ask about these recommendations"
                data-testid="ntsb-chat-panel"
                sx={{
                    display: chatOpen ? 'flex' : 'none',
                    flexDirection: 'column',
                    position: 'absolute',
                    right: 24,
                    bottom: 24,
                    width: 'min(480px, calc(100% - 48px))',
                    height: 'min(640px, calc(100% - 48px))',
                    zIndex: 5,
                    overflow: 'hidden',
                }}
            >
                <LLMChatDialog
                    title="Ask about these recommendations"
                    contextPrepend={contextPrepend}
                    contextMessage={`Answers cover the ${shown.length} recommendation${shown.length === 1 ? '' : 's'} the table shows.`}
                    fillHeight
                    headerIconsPaddingRight={4}
                    headerActions={
                        <IconButton size="small" aria-label="Close the conversation" onClick={(): void => setChatOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                    suggestedPrompts={SUGGESTED_PROMPTS}
                    messages={chatHistory as ChatMessage[]}
                    onMessagesChange={(messages: ChatMessage[]): void => setChatHistory(messages as PersistedChatMessage[])}
                    contextSent={chatContextSent}
                    onContextSentChange={setChatContextSent}
                />
            </Paper>
        </Box>
    );
};

export default NtsbRecommendationsTab;
